import { TranslationsEditor } from '../../components/TranslationsEditor'
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { formatMoney, resolveCurrencyCode } from '@/lib/money'
import { useProduct } from '../../context/ProductContext'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Dialog, DialogContent } from '../../components/ui/dialog'
import * as productApi from '../../services/productService'
import { normalizeProductImageUrls } from '../../utils/productImages'
import { SkeletonMetricsPanel, SkeletonProductDetail, SkeletonReviewPanel } from '../../components/ui/Skeleton'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'
import { useToast } from '../../context/ToastContext'
import { buildStorefrontUrl } from '../../utils/buildStorefrontUrl'

type TabId = 'overview' | 'reviews' | 'performance'

function StarRow({ rating }: { rating: number }) {
  return (
    <span className="inline-flex gap-0.5 text-[var(--warning)]" aria-hidden>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n}>{n <= rating ? '★' : '☆'}</span>
      ))}
    </span>
  )
}

const tabBtn =
  'rounded-full px-4 py-2 text-sm font-medium transition-colors border border-transparent data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=false]:text-muted-foreground data-[active=false]:hover:bg-muted'

function productMetricsTrendMaxUnits(
  buckets: { unitsSold: number }[] | undefined,
): number {
  if (!buckets?.length) return 1
  return Math.max(1, ...buckets.map((b) => b.unitsSold))
}

export default function ProductDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const {
    selectedProduct,
    getProductById,
    updateProduct,
    deleteProduct,
    mutationLoading,
    error,
    clearError,
  } = useProduct()
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const { pushToast } = useToast()
  const { currentStore, storeRecord } = useCurrentStore()
  const [tab, setTab] = useState<TabId>('overview')
  const [reviewsData, setReviewsData] = useState<Awaited<
    ReturnType<typeof productApi.fetchProductReviews>
  > | null>(null)
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [reviewActionId, setReviewActionId] = useState<number | null>(null)
  const [reviewActionError, setReviewActionError] = useState<string | null>(null)
  const [hideReviewTarget, setHideReviewTarget] = useState<{
    id: number
    authorName: string
  } | null>(null)
  const [metricsData, setMetricsData] = useState<Awaited<
    ReturnType<typeof productApi.fetchProductMetrics>
  > | null>(null)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [activeImageSrc, setActiveImageSrc] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    clearError()
    getProductById(id).catch(() => {})
  }, [id, getProductById, clearError])

  const loadReviews = useCallback(async () => {
    if (!id) return
    setReviewsLoading(true)
    try {
      const d = await productApi.fetchProductReviews(id)
      setReviewsData(d)
    } catch {
      setReviewsData(null)
    } finally {
      setReviewsLoading(false)
    }
  }, [id])

  const loadMetrics = useCallback(async () => {
    if (!id) return
    setMetricsLoading(true)
    try {
      const d = await productApi.fetchProductMetrics(id)
      setMetricsData(d)
    } catch {
      setMetricsData(null)
    } finally {
      setMetricsLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (tab === 'reviews' && id) loadReviews()
  }, [tab, id, loadReviews])

  const applyReviewVisibility = async (reviewId: number, hidden: boolean) => {
    if (!id) return
    setReviewActionError(null)
    setReviewActionId(reviewId)
    try {
      await productApi.setProductReviewHidden(id, reviewId, hidden)
      await loadReviews()
    } catch (e) {
      setReviewActionError(
        getUserFacingErrorMessage(e, hidden ? 'Could not hide review' : 'Could not show review'),
      )
      throw e
    } finally {
      setReviewActionId(null)
    }
  }

  const confirmHideReview = async () => {
    if (!hideReviewTarget) return
    await applyReviewVisibility(hideReviewTarget.id, true)
    setHideReviewTarget(null)
  }

  useEffect(() => {
    if (tab === 'performance' && id) loadMetrics()
  }, [tab, id, loadMetrics])

  async function handleDelete() {
    if (!id) return
    clearError()
    try {
      await deleteProduct(id)
      pushToast({ message: 'Product deleted', variant: 'success' })
      navigate('/products')
    } catch {
      /* error in context */
    }
  }

  async function handlePublish() {
    if (!id) return
    clearError()
    try {
      await updateProduct(id, { isActive: true })
      await getProductById(id)
    } catch {
      /* context */
    }
  }

  const images = normalizeProductImageUrls(selectedProduct?.images)
  const published = selectedProduct?.isActive === true
  const storeSubdomain = currentStore?.subdomain || storeRecord?.subdomain || ''
  const storefrontProductUrl =
    published && selectedProduct?.slug
      ? buildStorefrontUrl(storeSubdomain, `/product/${encodeURIComponent(selectedProduct.slug)}`)
      : null

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title={selectedProduct?.name ?? 'Product'}
          description="Catalog item for your primary store."
          action={
            id && selectedProduct ? (
              <div className="flex flex-wrap items-center gap-2">
                {published ? (
                  <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-medium text-primary ring-1 ring-primary/25">
                    Published
                  </span>
                ) : (
                  <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground ring-1 ring-border">
                    Draft
                  </span>
                )}
                {!published ? (
                  <Button type="button" size="sm" onClick={handlePublish} disabled={mutationLoading}>
                    {mutationLoading ? 'Publishing…' : 'Publish to storefront'}
                  </Button>
                ) : null}
                <Link to={`/products/${id}/edit`}>
                  <Button size="sm" variant="secondary">
                    Edit
                  </Button>
                </Link>
                {storefrontProductUrl ? (
                  <a
                    href={storefrontProductUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    View on storefront
                  </a>
                ) : null}
              </div>
            ) : null
          }
        />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {!selectedProduct ? (
          <SkeletonProductDetail />
        ) : (
          <>
            <div className="flex flex-wrap gap-2 border-b border-border pb-3">
              {(
                [
                  ['overview', 'Overview'],
                  ['reviews', 'Customer reviews'],
                  ['performance', 'Performance'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={tabBtn}
                  data-active={tab === key}
                  onClick={() => setTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {tab === 'overview' ? (
              <>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">Name</dt>
                    <dd className="text-sm font-medium text-foreground">{selectedProduct.name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">URL slug</dt>
                    <dd className="text-sm font-medium font-mono text-foreground break-all">{selectedProduct.slug}</dd>
                    <dd className="mt-1 text-xs text-muted-foreground">Set automatically when the product was created.</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">SKU</dt>
                    <dd className="text-sm font-medium text-foreground">{selectedProduct.sku}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">Price</dt>
                    <dd className="text-sm font-medium text-foreground tabular-nums">
                      {formatMoney(
                        Number(selectedProduct.price),
                        resolveCurrencyCode(selectedProduct.currency),
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">Stock</dt>
                    <dd className="text-sm font-medium text-foreground">{selectedProduct.stockQuantity}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase text-muted-foreground">Category slug</dt>
                    <dd className="text-sm font-medium text-foreground">
                      {selectedProduct.categorySlug ?? '—'}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs uppercase text-muted-foreground">Description</dt>
                    <dd className="text-sm text-foreground whitespace-pre-wrap">
                      {selectedProduct.description || '—'}
                    </dd>
                  </div>
                  {selectedProduct.otherDetails ? (
                    <div className="sm:col-span-2">
                      <dt className="text-xs uppercase text-muted-foreground">Other details (storefront)</dt>
                      <dd className="text-sm text-foreground whitespace-pre-wrap">
                        {selectedProduct.otherDetails}
                      </dd>
                    </div>
                  ) : null}
                </dl>

                {images.length ? (
                  <div className="mt-6">
                    <p className="text-xs uppercase text-muted-foreground mb-2">Images</p>
                    <div className="flex flex-wrap gap-3">
                      {images.map((src) => (
                        <button
                          key={src}
                          type="button"
                          onClick={() => setActiveImageSrc(src)}
                          className="block h-28 w-28 overflow-hidden rounded-xl border border-border bg-muted/30 shadow-sm"
                          aria-label="Open image preview"
                        >
                          <img src={src} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="mt-8 rounded-2xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground">Quick summary</p>
                  <p className="mt-1">
                    <strong>Customer reviews</strong> appear after shoppers purchase this product—they are not created
                    when you add the product. Use <strong>Performance</strong> for sales trends when orders are
                    integrated.
                  </p>
                </div>
              </>
            ) : null}

            {tab === 'reviews' ? (
              <div className="space-y-6">
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Ratings and written feedback come from <strong className="text-foreground">customers who bought this product</strong>
                  . Creating or editing a product here does not add reviews. When your storefront and order flow are
                  connected, eligible buyers will be able to submit reviews after purchase.
                </p>
                {reviewsLoading ? (
                  <SkeletonReviewPanel />
                ) : reviewsData ? (
                  <>
                    <div className="flex flex-wrap items-end justify-between gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm">
                      <div>
                        <p className="text-xs uppercase text-muted-foreground">Average rating</p>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-2xl font-semibold text-foreground">
                            {reviewsData.averageRating != null ? reviewsData.averageRating.toFixed(1) : '—'}
                          </span>
                          {reviewsData.averageRating != null ? (
                            <StarRow rating={Math.round(reviewsData.averageRating)} />
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {reviewsData.count} review{reviewsData.count === 1 ? '' : 's'}
                        </p>
                      </div>
                      <Button type="button" size="sm" variant="secondary" onClick={loadReviews}>
                        Refresh
                      </Button>
                    </div>
                    {reviewActionError ? (
                      <p className="text-sm text-destructive" role="alert">
                        {reviewActionError}
                      </p>
                    ) : null}
                    {reviewsData.items.length === 0 ? (
                      <p className="rounded-xl border border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
                        No customer reviews yet. Reviews will show here after purchasers submit feedback through your
                        storefront.
                      </p>
                    ) : (
                      <ul className="space-y-3">
                        {reviewsData.items.map((r) => (
                          <li
                            key={r.id}
                            className={`rounded-2xl border bg-card p-4 shadow-sm ${
                              r.isHidden ? 'border-amber-200/80 bg-amber-50/30' : 'border-border'
                            }`}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-medium text-foreground">{r.authorName}</span>
                                  <StarRow rating={r.rating} />
                                  {r.isHidden ? (
                                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900">
                                      Hidden
                                    </span>
                                  ) : null}
                                </div>
                                {r.title ? (
                                  <p className="mt-1 text-sm font-medium text-foreground">{r.title}</p>
                                ) : null}
                                {r.body ? (
                                  <p className="mt-2 text-sm text-foreground whitespace-pre-wrap">{r.body}</p>
                                ) : null}
                                <p className="mt-2 text-xs text-muted-foreground">
                                  {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
                                </p>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant={r.isHidden ? 'secondary' : 'outline'}
                                disabled={reviewActionId === r.id}
                                onClick={() => {
                                  if (r.isHidden) {
                                    void applyReviewVisibility(r.id, false)
                                  } else {
                                    setHideReviewTarget({ id: r.id, authorName: r.authorName })
                                  }
                                }}
                              >
                                {reviewActionId === r.id
                                  ? 'Saving…'
                                  : r.isHidden
                                    ? 'Show on storefront'
                                    : 'Hide from storefront'}
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Could not load reviews.</p>
                )}
              </div>
            ) : null}

            {tab === 'performance' ? (
              <div className="space-y-6">
                {metricsLoading ? (
                  <SkeletonMetricsPanel />
                ) : metricsData ? (
                  <>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/40 p-4 shadow-sm">
                        <p className="text-xs uppercase text-muted-foreground">Units sold</p>
                        <p className="mt-2 text-2xl font-semibold text-foreground">{metricsData.unitsSold}</p>
                      </div>
                      <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/40 p-4 shadow-sm">
                        <p className="text-xs uppercase text-muted-foreground">Revenue</p>
                        <p className="mt-2 text-2xl font-semibold text-foreground tabular-nums">
                          {formatMoney(
                            typeof metricsData.revenue === 'number' ? metricsData.revenue : 0,
                            resolveCurrencyCode(metricsData.currency),
                          )}
                        </p>
                      </div>
                      <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/40 p-4 shadow-sm">
                        <p className="text-xs uppercase text-muted-foreground">Last sale</p>
                        <p className="mt-2 text-lg font-medium text-foreground">
                          {metricsData.lastSaleAt ? new Date(metricsData.lastSaleAt).toLocaleString() : '—'}
                        </p>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-border bg-muted/30 p-4">
                      <p className="text-xs font-medium uppercase text-muted-foreground">Units sold (last 7 days)</p>
                      {metricsData.trend?.buckets?.length ? (
                        <>
                          <div
                            className="mt-4 flex h-32 items-end gap-1"
                            role="img"
                            aria-label="Daily units sold trend"
                          >
                            {(() => {
                              const maxUnits = productMetricsTrendMaxUnits(metricsData.trend.buckets)
                              return metricsData.trend.buckets.map((bucket) => {
                                const heightPct =
                                  bucket.unitsSold > 0 ? (bucket.unitsSold / maxUnits) * 100 : 4
                                const revenueLabel = formatMoney(
                                  bucket.revenue,
                                  resolveCurrencyCode(metricsData.currency),
                                )
                                return (
                                  <div
                                    key={bucket.date}
                                    className="flex-1 min-h-[4px] rounded-t-md bg-primary/50"
                                    style={{ height: `${heightPct}%` }}
                                    title={`${bucket.label}: ${bucket.unitsSold} units, ${revenueLabel}`}
                                    aria-label={`${bucket.label}: ${bucket.unitsSold} units sold`}
                                  />
                                )
                              })
                            })()}
                          </div>
                          <div className="mt-2 flex justify-between gap-1 text-[10px] text-muted-foreground">
                            {metricsData.trend.buckets.map((bucket) => (
                              <span key={bucket.date} className="flex-1 truncate text-center" title={bucket.date}>
                                {bucket.label}
                              </span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="mt-4 text-center text-sm text-muted-foreground">No trend data available.</p>
                      )}
                    </div>
                    {metricsData.message && metricsData.unitsSold === 0 ? (
                      <p className="text-sm text-muted-foreground">{metricsData.message}</p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Could not load metrics.</p>
                )}
              </div>
            ) : null}

            <div className="mt-8 border-t border-border pt-6">
              <p className="mb-3 text-sm text-muted-foreground">Delete this product from the catalog.</p>
              <Button
                type="button"
                variant="danger"
                disabled={mutationLoading}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete product
              </Button>
            </div>
          </>
        )}
      </Card>
      {id && selectedProduct ? (
        <TranslationsEditor
          entity="product"
          entityId={Number(id)}
          reloadKey={String(selectedProduct.updatedAt ?? '')}
          fields={[
            { key: 'name', label: 'Product name' },
            { key: 'description', label: 'Description', multiline: true, rows: 5 },
            { key: 'otherDetails', label: 'More details', multiline: true, rows: 4 },
          ]}
        />
      ) : null}
      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete this product?"
        variant="danger"
        confirmLabel="Delete product"
        cancelLabel="Cancel"
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={async () => {
          await handleDelete()
          setConfirmDeleteOpen(false)
        }}
        description={
          selectedProduct ? (
            <div className="space-y-3">
              <p>
                Permanently delete{' '}
                <strong className="font-semibold text-foreground">{selectedProduct.name}</strong>? This
                cannot be undone.
              </p>
            </div>
          ) : null
        }
      />
      <ConfirmModal
        open={hideReviewTarget != null}
        title="Hide this review?"
        confirmLabel="Hide from storefront"
        cancelLabel="Cancel"
        variant="primary"
        size="sm"
        onClose={() => setHideReviewTarget(null)}
        onConfirm={confirmHideReview}
        description={
          hideReviewTarget ? (
            <div className="space-y-3">
              <p>
                Hide the review from{' '}
                <strong className="font-semibold text-foreground">{hideReviewTarget.authorName}</strong> on your
                storefront?
              </p>
              <p className="text-muted-foreground">You can show it again later from this page.</p>
            </div>
          ) : null
        }
      />
      <Dialog open={Boolean(activeImageSrc)} onOpenChange={(open) => !open && setActiveImageSrc(null)}>
        <DialogContent className="max-w-5xl p-3 sm:p-4" showCloseButton>
          {activeImageSrc ? (
            <div className="max-h-[80vh] overflow-auto">
              <img src={activeImageSrc} alt="Product preview" className="h-auto max-h-[78vh] w-full rounded-md object-contain" />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}
